import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    AppBar,
    Toolbar,
    IconButton,
    Menu,
    MenuItem,
    Avatar,
    Alert,
    CircularProgress,
    Chip,
    Paper
} from '@mui/material';
import {
    AccountCircle,
    Logout,
    Assignment,
    School,
    Dashboard as DashboardIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { APIs } from '../../utilities/APIs';
import { useAuth } from '../../users/AuthContext';
import ApplicantNotices from '../../components/ApplicantNotices';
import useCustomToast from '../../hooks/useToast';

const ApplicantDashboard = () => {    
    const navigate = useNavigate();
    const { user, isAuthenticated, logout, makeAuthenticatedRequest } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);
    const [availableForms, setAvailableForms] = useState([]);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const showToast = useCustomToast();

    useEffect(() => {
        // Check if user is logged in as applicant
        if (!isAuthenticated || user?.userType !== 'APPLICANT') {
            navigate('/applicant/login');
            return;
        }

        loadDashboardData();
    }, [isAuthenticated, user, navigate]);    
    
        const loadDashboardData = async () => {
        try {
            setLoading(true);

            // Load available forms using authenticated request
            const formsResponse = await makeAuthenticatedRequest(APIs.applicant.getForms);

            if (formsResponse.status === 'success') {
                setAvailableForms(formsResponse.data || []);
            }

            // Load user's applications
            await loadMyApplications();

        } catch (error) {
            setError('Failed to load dashboard data');
            // console.error('Dashboard load error:', error);
            showToast('Error','Dashboard load error:'+error,'error')
        } finally {
            setLoading(false);
        }
    };

    const loadMyApplications = async () => {
        try {
            // Get user identifier for querying applications
            const userIdentifier = user?.email || user?.username || user?.name;
            if (!userIdentifier) {
                showToast('Error','No user identifier found for loading applications','error');
                return;
            }

            // Load applications from all types
            const applicationPromises = [
                makeAuthenticatedRequest(APIs.applicant.getMyApplications.undergraduate(userIdentifier)),
                makeAuthenticatedRequest(APIs.applicant.getMyApplications.graduate(userIdentifier)),
                makeAuthenticatedRequest(APIs.applicant.getMyApplications.shortCourse(userIdentifier))
            ];

            const responses = await Promise.allSettled(applicationPromises);

            let allApplications = [];
            responses.forEach((response, index) => {
                if (response.status === 'fulfilled' && response.value?.status === 'success') {
                    const apps = response.value.data || [];
                    // Add application type identifier
                    const types = ['Undergraduate', 'Graduate', 'Short Course'];
                    apps.forEach(app => {
                        app.applicationType = types[index];
                    });
                    allApplications = [...allApplications, ...apps];
                }
            });

            setApplications(allApplications);

        } catch (error) {
            showToast('Error','loading applications'+error,'error')
        }
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };    const handleLogout = () => {
        logout('APPLICANT');
        handleMenuClose();
        // No need to navigate since logout function will handle redirect
    };const handleApplyToForm = (formId) => {
        navigate(`/applicant/form/${formId}`);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* App Bar */}
            <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <Toolbar>
                    <DashboardIcon sx={{ mr: 2 }} />
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        Applicant Portal
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>                        <Typography variant="body2" sx={{ mr: 2 }}>
                            Welcome, {user?.firstName || user?.username || 'Applicant'}
                        </Typography>
                        <IconButton
                            size="large"
                            aria-label="account menu"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleMenuOpen}
                            color="inherit"
                        >
                            <AccountCircle />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                        >
                            <MenuItem onClick={handleMenuClose}>
                                <AccountCircle sx={{ mr: 2 }} />
                                Profile
                            </MenuItem>
                            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                                <Logout sx={{ mr: 2 }} />
                                Logout
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>                )}

                {/* Notices Section */}
                <ApplicantNotices page="DASHBOARD" maxNotices={5} />

                {/* Welcome Section */}
                <Paper sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
                    <Typography variant="h4" gutterBottom>
                        Welcome to the UNIK Application Portal
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Apply for academic programs, track your applications, and manage your student profile.
                    </Typography>
                </Paper>

                <Grid container spacing={4}>
                    {/* Quick Stats */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Assignment color="primary" sx={{ mr: 2 }} />
                                    <Typography variant="h6">
                                        Available Forms
                                    </Typography>
                                </Box>
                                <Typography variant="h3" color="primary">
                                    {availableForms.length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Forms you can apply to
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <School color="success" sx={{ mr: 2 }} />
                                    <Typography variant="h6">
                                        My Applications
                                    </Typography>
                                </Box>
                                <Typography variant="h3" color="success.main">
                                    {applications.length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Submitted applications
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <AccountCircle color="info" sx={{ mr: 2 }} />
                                    <Typography variant="h6">
                                        Profile Status
                                    </Typography>
                                </Box>
                                <Chip
                                    label="Verified"
                                    color="success"
                                    sx={{ fontSize: '1rem', height: '2rem' }}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Email verified
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Available Forms */}
                    <Grid item xs={12}>
                        <Card>                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h5">
                                        Available Application Forms
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={() => navigate('/applicant/forms')}
                                        sx={{
                                            backgroundColor: 'primary.main',
                                            '&:hover': {
                                                backgroundColor: 'primary.dark'
                                            }
                                        }}
                                    >
                                        View All Forms
                                    </Button>
                                </Box>
                                {availableForms.length === 0 ? (
                                    <Alert severity="info">
                                        No application forms are currently available. Please check back later.
                                    </Alert>
                                ) : (
                                    <Grid container spacing={2}>
                                        {availableForms.slice(0, 3).map((form) => (
                                            <Grid item xs={12} sm={6} md={4} key={form.id}>
                                                <Card variant="outlined" sx={{ height: '100%' }}>
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom>
                                                            {form.title || form.name}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                            {form.description || 'Application form'}
                                                        </Typography>
                                                        <Chip
                                                            label={form.type || 'General'}
                                                            size="small"
                                                            color="primary"
                                                            sx={{ mb: 2 }}
                                                        />
                                                        <Box sx={{ mt: 2 }}>
                                                            <Button
                                                                variant="contained"
                                                                startIcon={<AddIcon />}
                                                                onClick={() => handleApplyToForm(form.id)}
                                                                fullWidth
                                                            >
                                                                Apply Now
                                                            </Button>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {availableForms.length > 3 && (
                                            <Grid item xs={12}>
                                                <Box sx={{ textAlign: 'center', mt: 2 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Showing 3 of {availableForms.length} forms
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* My Applications */}
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h5" gutterBottom>
                                    My Applications
                                </Typography>
                                {applications.length === 0 ? (
                                    <Alert severity="info">
                                        You haven't submitted any applications yet. Start by applying to an available form above.
                                    </Alert>
                                ) : (
                                    <Grid container spacing={2}>
                                        {applications.map((application) => (
                                            <Grid item xs={12} sm={6} md={4} key={application.id}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom>
                                                            {application.formTitle}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Submitted: {new Date(application.submittedAt).toLocaleDateString()}
                                                        </Typography>
                                                        <Chip
                                                            label={application.status}
                                                            color={
                                                                application.status === 'APPROVED' ? 'success' :
                                                                application.status === 'REJECTED' ? 'error' :
                                                                'warning'
                                                            }
                                                            sx={{ mt: 1 }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default ApplicantDashboard;
